import { useEffect } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { GET_PROVEEDOR, DELETE_PROVEEDOR } from './gql.js';

function formatCuit(value) {
  const digits = String(value || '')
    .replace(/[^\d]/g, '')
    .slice(0, 11);

  if (digits.length <= 2) return digits;
  if (digits.length <= 10) return `${digits.slice(0, 2)}-${digits.slice(2, 10)}`;
  return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
}

export default function ProveedorDetail({ proveedorId, onBack, onEdit }) {
  const [{ data, fetching, error }, reexecute] = useQuery({ query: GET_PROVEEDOR, variables: { id: proveedorId } });
  const [, deleteProveedor] = useMutation(DELETE_PROVEEDOR);

  useEffect(() => {
    reexecute({ requestPolicy: 'network-only' });
  }, [proveedorId, reexecute]);

  const proveedor = data?.getProveedor;

  const handleDelete = async () => {
    if (!proveedorId) return;
    const confirmation = window.confirm('¿Estás seguro de que querés eliminar este proveedor?');
    if (!confirmation) return;
    await deleteProveedor({ id: proveedorId });
    onBack?.();
  };

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title={proveedor?.razonSocial ?? 'Proveedor'}
        subtitle="Visualizá los datos principales y el historial de contacto."
        actions={
          <button
            type="button"
            onClick={() => onEdit?.(proveedor)}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Editar
          </button>
        }
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        {fetching ? (
          <p className="text-sm text-muted">Cargando información…</p>
        ) : error ? (
          <p className="text-sm text-red-300">{error.message}</p>
        ) : proveedor ? (
          <dl className="grid grid-cols-12 gap-4 text-sm text-[rgb(var(--fg))]">
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">CUIT</dt>
              <dd className="mt-1">{formatCuit(proveedor.cuit)}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Condición IVA</dt>
              <dd className="mt-1">{proveedor.condicionIVA}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Email</dt>
              <dd className="mt-1">{proveedor.email || '—'}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Teléfono</dt>
              <dd className="mt-1">{proveedor.telefono || '—'}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Dirección</dt>
              <dd className="mt-1">{proveedor.direccion || '—'}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Localidad</dt>
              <dd className="mt-1">{proveedor.localidad || '—'}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Provincia</dt>
              <dd className="mt-1">{proveedor.provincia || '—'}</dd>
            </div>
            <div className="col-span-12 md:col-span-6">
              <dt className="font-medium text-muted">Estado</dt>
              <dd className="mt-1">{proveedor.activo ? 'Activo' : 'Inactivo'}</dd>
            </div>
            <div className="col-span-12">
              <dt className="font-medium text-muted">Notas</dt>
              <dd className="mt-1 whitespace-pre-line">{proveedor.notas || '—'}</dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-muted">No encontramos información del proveedor.</p>
        )}
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Volver
          </button>
        }
        right={
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onEdit?.(proveedor)}
              className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              Editar proveedor
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="rounded-xl bg-red-600/90 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
            >
              Eliminar
            </button>
          </div>
        }
      />
    </div>
  );
}
